import React from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { connectionsHealth } from "@/lib/api";
import { useClient } from "@/context/ClientContext";
import { PageHeader, PageContainer } from "@/components/common/PageHeader";
import { Button } from "@/components/ui/button";
import { ArrowsClockwise } from "@phosphor-icons/react";

const S = {
  connected: { emoji: "🟢", label: "Connected", cls: "text-emerald-400" },
  syncing: { emoji: "🔄", label: "Syncing", cls: "text-sky-300" },
  needs_attention: { emoji: "🟡", label: "Needs attention", cls: "text-amber-400" },
  token_expired: { emoji: "⚠️", label: "Token expired", cls: "text-amber-400" },
  disconnected: { emoji: "🔴", label: "Disconnected", cls: "text-red-400" },
};
const fmtT = (iso) => (iso ? new Date(iso).toLocaleString() : "—");

export default function ConnectionsHealthPage() {
  const { activeId } = useClient();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data: d, isFetching } = useQuery({
    queryKey: ["connections-health", activeId],
    queryFn: () => connectionsHealth(activeId, false),
    enabled: !!activeId,
    refetchInterval: 120000,
  });
  const recheck = async () => {
    await connectionsHealth(activeId, true);
    qc.invalidateQueries({ queryKey: ["connections-health", activeId] });
  };

  const items = d?.items || [];
  const counts = d?.counts || {};

  return (
    <PageContainer>
      <PageHeader eyebrow="System" title="Connections Health"
        subtitle="Live health of every integration — checked against the real APIs with sanitized diagnostics. Tokens are never displayed.">
        <Button data-testid="health-recheck-btn" variant="outline" onClick={recheck} disabled={isFetching}
          className="border-white/15 bg-transparent hover:bg-white/5 text-white">
          <ArrowsClockwise size={16} className={`mr-1.5 ${isFetching ? "animate-spin" : ""}`} /> Re-check all
        </Button>
      </PageHeader>

      <div className="flex flex-wrap gap-3 mb-5" data-testid="health-summary">
        {Object.entries(S).map(([k, v]) => (
          <span key={k} className="text-xs text-[#9a9a9a]">{v.emoji} {counts[k] || 0} {v.label.toLowerCase()}</span>
        ))}
        <span className="text-xs text-[#5f5f5f] ml-auto">Last check: {fmtT(d?.checked_at)}</span>
      </div>

      <div className="space-y-3">
        {items.map((it) => {
          const s = S[it.status] || S.disconnected;
          const needsAction = ["token_expired", "disconnected", "needs_attention"].includes(it.status);
          return (
            <div key={it.key} className="card-matte p-4 flex items-center gap-4 flex-wrap" data-testid={`health-${it.key}`}>
              <span className="text-xl w-8 text-center">{s.emoji}</span>
              <div className="flex-1 min-w-[200px]">
                <div className="flex items-center gap-2">
                  <span className="font-head font-semibold">{it.name}</span>
                  <span className={`text-[11px] font-semibold ${s.cls}`} data-testid={`health-status-${it.key}`}>{s.label}</span>
                </div>
                <div className="text-xs text-[#9a9a9a] mt-0.5">{it.error || it.detail || "—"}</div>
              </div>
              <div className="text-right text-[11px] text-[#7a7a7a] leading-relaxed">
                <div>Last sync: <span className="text-[#cfcfcf]">{fmtT(it.last_sync)}</span></div>
                <div>Last check: <span className="text-[#cfcfcf]">{fmtT(it.last_check)}</span></div>
              </div>
              {it.reconnect_route && needsAction && (
                <Button data-testid={`health-reconnect-${it.key}`} size="sm" onClick={() => navigate(it.reconnect_route)}
                  className="bg-[#6B3DFF] hover:bg-[#A56CFF] text-white shrink-0">
                  {it.status === "token_expired" ? "Reconnect" : it.status === "disconnected" ? "Connect" : "Review"}
                </Button>
              )}
            </div>
          );
        })}
        {!items.length && <div className="text-center text-[#7a7a7a] py-16">Running health checks…</div>}
      </div>
    </PageContainer>
  );
}
